// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";

// Layout
import Navbar from "./components/Layout/Navbar";
import Footer from "./components/Layout/Footer";

// Public Pages
import Home from "./pages/Public/Home";
import About from "./pages/Public/About";
import Services from "./pages/Public/Services";
import Contact from "./pages/Public/Contact";
import Login from "./pages/Public/Login";
import Register from "./pages/Public/Register";
import ForgotPassword from "./pages/Public/ForgotPassword";
import ResetPassword from "./pages/Public/ResetPassword";
import PublicTracking from "./pages/Public/PublicTracking";
import TrackingPage from "./pages/Tracking/TrackingPage";
import ParcelImport from "./pages/Operator/ParcelImport";
import Store from "./pages/Public/Store";

// ✅ NOUVELLES PAGES
import FAQ from "./pages/Public/FAQ";
import Confidentialite from "./pages/Public/Confidentialite";
import MentionsLegales from "./pages/Public/MentionsLegales";
import CGV from "./pages/Public/CGV";

// Client Pages
import ClientDashboard from "./pages/Client/ClientDashboard";
import MyParcels from "./pages/Client/MyParcels";
import Profile from "./pages/Client/Profile";

// Operator Pages
import OperatorDashboard from "./pages/Operator/OperatorDashboard";
import ParcelList from "./pages/Operator/ParcelList";
import ParcelCreate from "./pages/Operator/ParcelCreate";

// Admin Pages
import AdminDashboard from "./pages/Admin/AdminDashboard";

// SuperAdmin Pages
import SuperAdminDashboard from "./pages/SuperAdmin/SuperAdminDashboard";
import StoreManagement from "./pages/SuperAdmin/StoreManagement";

// Private Route Component
const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-danger" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-2 text-muted">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Login />;

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="text-center py-5">
        <h1 className="text-danger">🚫 Accès non autorisé</h1>
        <p className="text-muted">
          Vous n'avez pas les droits nécessaires pour accéder à cette page.
        </p>
      </div>
    );
  }

  return children;
};

function AppContent() {
  return (
    <Router>
      <div className="d-flex flex-column min-vh-100">
        <Navbar />
        <main className="flex-grow-1">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* ✅ NOUVELLES ROUTES LEGALES */}
            <Route path="/faq" element={<FAQ />} />
            <Route path="/confidentialite" element={<Confidentialite />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/cgv" element={<CGV />} />

            <Route path="/tracking" element={<TrackingPage />} />
            <Route
              path="/tracking/:trackingNumber"
              element={<TrackingPage />}
            />
            <Route path="/store" element={<Store />} />

            {/* Client Routes */}
            <Route
              path="/client"
              element={
                <PrivateRoute allowedRoles={["client"]}>
                  <ClientDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/client/parcels"
              element={
                <PrivateRoute allowedRoles={["client"]}>
                  <MyParcels />
                </PrivateRoute>
              }
            />
            <Route
              path="/client/profile"
              element={
                <PrivateRoute allowedRoles={["client"]}>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute allowedRoles={["client"]}>
                  <ClientDashboard />
                </PrivateRoute>
              }
            />

            {/* Operator Routes */}
            <Route
              path="/operator"
              element={
                <PrivateRoute allowedRoles={["operator"]}>
                  <OperatorDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/operator/parcels"
              element={
                <PrivateRoute allowedRoles={["operator"]}>
                  <ParcelList />
                </PrivateRoute>
              }
            />
            <Route
              path="/operator/parcels/create"
              element={
                <PrivateRoute allowedRoles={["operator"]}>
                  <ParcelCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/operator/parcels/import"
              element={
                <PrivateRoute allowedRoles={["operator"]}>
                  <ParcelImport />
                </PrivateRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <PrivateRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />

            {/* SuperAdmin Routes */}
            <Route
              path="/superadmin"
              element={
                <PrivateRoute allowedRoles={["superadmin"]}>
                  <SuperAdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/superadmin/StoreManagement"
              element={
                <PrivateRoute allowedRoles={["superadmin"]}>
                  <StoreManagement />
                </PrivateRoute>
              }
            />
          </Routes>
        </main>
        <Footer />
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
